import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Play, Eye, Lock, UserPlus, MessageCircle, Send, X, Coins, Instagram, Facebook, Clock, CheckCircle, Rocket, ExternalLink, Share2, Copy, Camera, Upload, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const WATCH_TIME_REQUIRED = 80; // 1:20 in seconds

const VideoPage = () => {
  const { user, token, isAuthenticated, refreshUser } = useAuth();
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [upcomingLaunches, setUpcomingLaunches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followingArtists, setFollowingArtists] = useState([]);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState("");
  const [userComments, setUserComments] = useState([]);
  const [socialTasks, setSocialTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [dailyStats, setDailyStats] = useState(null);
  const [youtubeConnected, setYoutubeConnected] = useState(false);
  const [shareCampaigns, setShareCampaigns] = useState([]);
  const [completedCampaigns, setCompletedCampaigns] = useState([]);
  const [youtubeCampaigns, setYoutubeCampaigns] = useState([]);
  const [campaignProgress, setCampaignProgress] = useState({});
  const [watchTimers, setWatchTimers] = useState({});
  const [canConfirmView, setCanConfirmView] = useState({});
  const [campaignScreenshots, setCampaignScreenshots] = useState({});
  const [uploadingScreenshot, setUploadingScreenshot] = useState({});
  const [taskScreenshots, setTaskScreenshots] = useState({});
  const [shareScreenshots, setShareScreenshots] = useState({});
  const [uploadingTaskScreenshot, setUploadingTaskScreenshot] = useState({});
  const [uploadingShareScreenshot, setUploadingShareScreenshot] = useState({});
  const screenshotInputRefs = useRef({});
  const taskScreenshotInputRefs = useRef({});
  const shareScreenshotInputRefs = useRef({});
  
  // Watch time tracking
  const [watchTime, setWatchTime] = useState(0);
  const [isWatching, setIsWatching] = useState(false);
  const [canClaim, setCanClaim] = useState(false);
  const [hasClaimed, setHasClaimed] = useState(false);
  const watchTimerRef = useRef(null);

  useEffect(() => {
    fetchVideos();
    fetchSocialTasks();
    fetchShareCampaigns();
    fetchYoutubeCampaigns();
    if (isAuthenticated && token) {
      fetchFollowing();
      fetchCompletedTasks();
      fetchDailyStats();
      fetchYoutubeStatus();
    }
  }, [isAuthenticated, token]);

  const fetchYoutubeCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/youtube-campaigns/active`);
      setYoutubeCampaigns(response.data);
      // Fetch progress for each campaign if authenticated
      if (isAuthenticated && token) {
        response.data.forEach(campaign => {
          fetchCampaignProgress(campaign.id);
        });
      }
    } catch (error) {
      console.error("Error fetching video campaigns:", error);
    }
  };

  const fetchCampaignProgress = async (campaignId) => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/youtube-campaigns/${campaignId}/my-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaignProgress(prev => ({ ...prev, [campaignId]: response.data }));
      // Also fetch screenshots
      fetchCampaignScreenshots(campaignId);
    } catch (error) {
      console.error("Error fetching campaign progress:", error);
    }
  };

  const startCampaign = async (campaignId) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para participar");
      return;
    }
    try {
      const response = await axios.post(`${API}/youtube-campaigns/${campaignId}/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchCampaignProgress(campaignId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al iniciar participación");
    }
  };

  const startWatchTimer = (campaignId) => {
    // Start 80 second timer (1:20)
    setWatchTimers(prev => ({ ...prev, [campaignId]: 80 }));
    setCanConfirmView(prev => ({ ...prev, [campaignId]: false }));
    
    const interval = setInterval(() => {
      setWatchTimers(prev => {
        const currentTime = prev[campaignId] || 0;
        if (currentTime <= 1) {
          clearInterval(interval);
          setCanConfirmView(p => ({ ...p, [campaignId]: true }));
          toast.success("✅ ¡Tiempo completado! Ahora puedes confirmar tu vista");
          return { ...prev, [campaignId]: 0 };
        }
        return { ...prev, [campaignId]: currentTime - 1 };
      });
    }, 1000);
  };

  const completeTask = async (campaignId, task) => {
    try {
      const response = await axios.post(`${API}/youtube-campaigns/${campaignId}/complete-task?task=${task}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchCampaignProgress(campaignId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al completar tarea");
    }
  };

  const recordView = async (campaignId) => {
    try {
      const response = await axios.post(`${API}/youtube-campaigns/${campaignId}/record-view`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setCanConfirmView(prev => ({ ...prev, [campaignId]: false }));
      fetchCampaignProgress(campaignId);
      if (response.data.completed) {
        refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar vista");
    }
  };

  // Screenshot upload functions
  const fetchCampaignScreenshots = async (campaignId) => {
    if (!isAuthenticated) return;
    try {
      const response = await axios.get(`${API}/youtube-campaigns/${campaignId}/my-screenshots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCampaignScreenshots(prev => ({ ...prev, [campaignId]: response.data }));
    } catch (error) {
      console.error("Error fetching screenshots:", error);
    }
  };

  const handleScreenshotUpload = async (campaignId, file) => {
    if (!file) return;
    
    setUploadingScreenshot(prev => ({ ...prev, [campaignId]: true }));
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(
        `${API}/youtube-campaigns/${campaignId}/upload-screenshot`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      toast.success(response.data.message);
      fetchCampaignScreenshots(campaignId);
      fetchCampaignProgress(campaignId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir captura");
    } finally {
      setUploadingScreenshot(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const triggerScreenshotInput = (campaignId) => {
    if (screenshotInputRefs.current[campaignId]) {
      screenshotInputRefs.current[campaignId].click();
    }
  };

  // Upload screenshot for social task
  const handleTaskScreenshotUpload = async (taskId, file) => {
    if (!file || !token) return;
    
    setUploadingTaskScreenshot(prev => ({ ...prev, [taskId]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/social-tasks/${taskId}/upload-screenshot`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      fetchTaskScreenshotStatus(taskId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir captura");
    } finally {
      setUploadingTaskScreenshot(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const fetchTaskScreenshotStatus = async (taskId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/social-tasks/${taskId}/my-screenshot`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskScreenshots(prev => ({ ...prev, [taskId]: response.data }));
    } catch (error) {
      console.error("Error fetching task screenshot:", error);
    }
  };

  const triggerTaskScreenshotInput = (taskId) => {
    if (taskScreenshotInputRefs.current[taskId]) {
      taskScreenshotInputRefs.current[taskId].click();
    }
  };

  // Upload screenshot for share campaign
  const handleShareScreenshotUpload = async (campaignId, file) => {
    if (!file || !token) return;
    
    setUploadingShareScreenshot(prev => ({ ...prev, [campaignId]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/share-campaigns/${campaignId}/upload-screenshot`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(response.data.message);
      fetchShareScreenshotStatus(campaignId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al subir captura");
    } finally {
      setUploadingShareScreenshot(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const fetchShareScreenshotStatus = async (campaignId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/share-campaigns/${campaignId}/my-screenshot`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareScreenshots(prev => ({ ...prev, [campaignId]: response.data }));
    } catch (error) {
      console.error("Error fetching share screenshot:", error);
    }
  };

  const triggerShareScreenshotInput = (campaignId) => {
    if (shareScreenshotInputRefs.current[campaignId]) {
      shareScreenshotInputRefs.current[campaignId].click();
    }
  };

  const fetchShareCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/share-campaigns`);
      setShareCampaigns(response.data);
    } catch (error) {
      console.error("Error fetching share campaigns:", error);
    }
  };

  const handleShareCampaign = async (campaignId) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para completar esta tarea");
      return;
    }
    // Now just opens share - user must upload screenshot
    toast.info("Comparte y luego sube una captura de pantalla como prueba");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("URL copiada al portapapeles");
  };

  const openShareWindow = (platform, url, text = "") => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(text);
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      instagram: `https://www.instagram.com/`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      tiktok: `https://www.tiktok.com/`
    };
    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const getPlatformIcon = (platform) => {
    switch(platform?.toLowerCase()) {
      case 'facebook': return <Facebook className="w-5 h-5 text-[#1877F2]" />;
      case 'instagram': return <Instagram className="w-5 h-5 text-[#E1306C]" />;
      case 'tiktok': return <TikTokIcon className="w-5 h-5" />;
      case 'youtube': return <Play className="w-5 h-5 text-[#FF0000]" />;
      case 'whatsapp': return <span className="text-lg">💬</span>;
      default: return <Share2 className="w-5 h-5" />;
    }
  };

  const getPlatformColor = (platform) => {
    switch(platform?.toLowerCase()) {
      case 'facebook': return 'bg-[#1877F2]';
      case 'instagram': return 'bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737]';
      case 'tiktok': return 'bg-black border border-white/20';
      case 'youtube': return 'bg-gradient-to-r from-[#FF0000] to-[#CC0000]';
      case 'whatsapp': return 'bg-[#25D366]';
      default: return 'bg-gray-600';
    }
  };

  const fetchYoutubeStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/youtube/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYoutubeConnected(response.data.connected);
    } catch (error) {
      console.error("Error fetching YouTube status:", error);
    }
  };

  const fetchUpcomingLaunches = async () => {
    try {
      const response = await axios.get(`${API}/launch-events/upcoming`);
      setUpcomingLaunches(response.data);
    } catch (error) {
      console.error("Error fetching launches:", error);
    }
  };

  // Fetch launches periodically
  useEffect(() => {
    fetchUpcomingLaunches();
    const interval = setInterval(fetchUpcomingLaunches, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Vibration effect when launch is within 5 minutes
  useEffect(() => {
    const hasLaunchSoon = upcomingLaunches.some(launch => launch.can_go_to_youtube && !launch.task_expired);
    
    if (hasLaunchSoon && navigator.vibrate) {
      // Vibrate pattern: vibrate 200ms, pause 100ms, vibrate 200ms - repeat every 10 seconds
      const vibratePattern = () => {
        navigator.vibrate([200, 100, 200]);
      };
      
      vibratePattern(); // Initial vibration
      const vibrationInterval = setInterval(vibratePattern, 10000); // Every 10 seconds
      
      return () => {
        clearInterval(vibrationInterval);
        navigator.vibrate(0); // Stop any ongoing vibration
      };
    }
  }, [upcomingLaunches]);

  const handleConfirmAttendance = async (eventId) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para confirmar asistencia");
      return;
    }

    if (user?.tier !== "pro") {
      toast.error("Solo usuarios Pro pueden confirmar asistencia", {
        description: "Actualiza a Pro para acceder a esta función"
      });
      return;
    }

    try {
      const response = await axios.post(
        `${API}/launch-events/${eventId}/confirm-attendance`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message, {
        description: "¡Te notificaremos cuando comience el lanzamiento!"
      });
      
      fetchUpcomingLaunches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al confirmar asistencia");
    }
  };

  const handleJoinLaunch = async (eventId) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para unirte al lanzamiento");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/launch-events/${eventId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message, {
        description: `Nuevo balance: RD$${response.data.new_balance.toFixed(2)}`
      });
      
      // Open YouTube in new tab
      window.open(response.data.youtube_url, "_blank");
      
      await refreshUser();
      fetchUpcomingLaunches();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al unirse al lanzamiento");
    }
  };

  // Watch time timer
  useEffect(() => {
    if (isWatching && !canClaim && !hasClaimed) {
      watchTimerRef.current = setInterval(() => {
        setWatchTime(prev => {
          const newTime = prev + 1;
          if (newTime >= WATCH_TIME_REQUIRED) {
            setCanClaim(true);
            clearInterval(watchTimerRef.current);
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (watchTimerRef.current) {
        clearInterval(watchTimerRef.current);
      }
    };
  }, [isWatching, canClaim, hasClaimed]);

  // State for tracking video views
  const [videoViews, setVideoViews] = useState({});

  // Fetch video view status
  const fetchVideoViewStatus = async (videoId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/videos/${videoId}/my-views`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideoViews(prev => ({ ...prev, [videoId]: response.data }));
    } catch (error) {
      console.error("Error fetching video views:", error);
    }
  };

  // Fetch all video views on load
  useEffect(() => {
    if (isAuthenticated && token && videos.length > 0) {
      videos.forEach(video => fetchVideoViewStatus(video.id));
    }
  }, [isAuthenticated, token, videos]);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyStats = async () => {
    try {
      const response = await axios.get(`${API}/users/daily-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailyStats(response.data);
    } catch (error) {
      console.error("Error fetching daily stats:", error);
    }
  };

  const fetchSocialTasks = async () => {
    try {
      const response = await axios.get(`${API}/social-tasks`);
      setSocialTasks(response.data);
    } catch (error) {
      console.error("Error fetching social tasks:", error);
    }
  };

  const fetchCompletedTasks = async () => {
    try {
      const response = await axios.get(`${API}/social-tasks/completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedTasks(response.data.completed || []);
    } catch (error) {
      console.error("Error fetching completed tasks:", error);
    }
  };

  const fetchFollowing = async () => {
    try {
      const response = await axios.get(`${API}/users/following`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFollowingArtists(response.data.following || []);
    } catch (error) {
      console.error("Error fetching following:", error);
    }
  };

  const fetchComments = async (videoId) => {
    try {
      const response = await axios.get(`${API}/videos/${videoId}/comments`);
      setComments(prev => ({ ...prev, [videoId]: response.data }));
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOpenVideo = (video) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para ganar dinero viendo videos");
      return;
    }

    setSelectedVideo(video);
    setWatchTime(0);
    setIsWatching(true);
    setCanClaim(false);
    setHasClaimed(false);
    fetchComments(video.id);
  };

  const handleClaimReward = async () => {
    if (!canClaim || hasClaimed) return;

    try {
      const response = await axios.post(
        `${API}/videos/${selectedVideo.id}/view?watch_time=${watchTime}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.earned > 0) {
        toast.success(response.data.message, {
          description: `Balance: RD$${response.data.new_balance.toFixed(2)} | Vistas restantes hoy: ${response.data.videos_remaining_today}`
        });
        setHasClaimed(true);
        fetchDailyStats();
        // Refresh video views
        fetchVideoViewStatus(selectedVideo.id);
      } else {
        toast.info(response.data.message);
      }
      
      await refreshUser();
      fetchVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reclamar recompensa");
    }
  };

  const handleFollowArtist = async (artistId, artistName, youtubeChannel) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para seguir artistas");
      return;
    }

    try {
      // Try to subscribe on YouTube if connected and channel URL exists
      if (youtubeConnected && youtubeChannel) {
        try {
          await axios.post(
            `${API}/youtube/subscribe`,
            null,
            { 
              params: { channel_url: youtubeChannel },
              headers: { Authorization: `Bearer ${token}` } 
            }
          );
          toast.success("¡Suscrito al canal de YouTube!");
        } catch (ytError) {
          console.log("YouTube subscribe failed:", ytError);
          if (ytError.response?.data?.detail?.includes("Ya estás suscrito")) {
            toast.info("Ya estás suscrito a este canal en YouTube");
          }
        }
      }
      
      // Always follow locally for earnings
      const response = await axios.post(
        `${API}/artists/${artistId}/follow`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message, {
        description: `Nuevo balance: RD$${response.data.new_balance.toFixed(2)}`
      });
      
      setFollowingArtists(prev => [...prev, artistId]);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al seguir al artista");
    }
  };

  const handleComment = async (videoId) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para comentar");
      return;
    }

    if (!newComment.trim()) {
      toast.error("Escribe un comentario");
      return;
    }

    try {
      // Try to post to YouTube if connected
      if (youtubeConnected && selectedVideo?.youtube_url) {
        try {
          await axios.post(
            `${API}/youtube/comment`,
            null,
            { 
              params: {
                video_youtube_url: selectedVideo.youtube_url,
                comment_text: newComment
              },
              headers: { Authorization: `Bearer ${token}` } 
            }
          );
          toast.success("¡Comentario publicado en YouTube!");
        } catch (ytError) {
          console.log("YouTube comment failed, posting locally:", ytError);
          toast.info("Comentario guardado localmente (no se pudo publicar en YouTube)");
        }
      }
      
      // Always save comment locally for earnings
      await axios.post(
        `${API}/videos/${videoId}/comments`,
        { video_id: videoId, content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("¡Ganaste RD$3 por tu comentario!");
      setNewComment("");
      setUserComments(prev => [...prev, videoId]);
      fetchComments(videoId);
      await refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al comentar");
    }
  };

  const handleCompleteSocialTask = async (task) => {
    if (!isAuthenticated) {
      toast.error("Inicia sesión para completar tareas");
      return;
    }

    window.open(task.profile_url, "_blank");

    setTimeout(async () => {
      try {
        const response = await axios.post(
          `${API}/social-tasks/${task.id}/complete`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(response.data.message, {
          description: `Nuevo balance: RD$${response.data.new_balance.toFixed(2)}`
        });
        
        setCompletedTasks(prev => [...prev, task.id]);
        await refreshUser();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Error al completar tarea");
      }
    }, 1000);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    setNewComment("");
    setIsWatching(false);
    setWatchTime(0);
    setCanClaim(false);
    setHasClaimed(false);
    if (watchTimerRef.current) {
      clearInterval(watchTimerRef.current);
    }
  };

  const hasCommented = (videoId) => {
    const videoComments = comments[videoId] || [];
    return videoComments.some(c => c.user_id === user?.id) || userComments.includes(videoId);
  };

  const isVideoWatchedToday = (videoId) => {
    return dailyStats?.watched_video_ids?.includes(videoId) || false;
  };

  const getTasksByPlatform = (platform) => {
    return socialTasks.filter(t => t.platform === platform);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D946EF]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 animate-fadeInUp" data-testid="video-page">
      {/* User Earnings Header */}
      {isAuthenticated && user && (
        <div className="sticky top-0 z-40 glass border-b border-white/10">
          <div className="max-w-lg mx-auto p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#22C55E] to-[#10B981] flex items-center justify-center">
                  <Coins className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Tu Balance</p>
                  <p className="text-2xl font-bold text-[#22C55E]">RD${user.balance?.toFixed(2) || "0.00"}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Total ganado</p>
                <p className="text-lg font-bold">RD${user.total_earnings?.toFixed(2) || "0.00"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="p-4">
        <h1 className="text-3xl font-bold mb-6 gradient-text">Gana Dinero</h1>

        {!isAuthenticated && (
          <Card className="bg-[#1E1E1E] border-[#D946EF]/50 mb-6">
            <CardContent className="p-4 flex items-center gap-3">
              <Lock className="w-5 h-5 text-[#D946EF]" />
              <p className="text-sm">
                <span className="text-[#D946EF] font-bold">Inicia sesión</span> para ganar dinero
              </p>
            </CardContent>
          </Card>
        )}

        {/* Level Info */}
        {isAuthenticated && dailyStats && (
          <Card className="bg-gradient-to-r from-[#D946EF]/20 to-[#8B5CF6]/20 border-white/10 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-gray-400">Tu Nivel</p>
                  <p className="text-xl font-bold">Nivel {dailyStats.level}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Videos restantes hoy</p>
                  <p className="text-2xl font-bold text-[#22C55E]">
                    {dailyStats.videos_remaining_today} / {dailyStats.daily_limit}
                  </p>
                </div>
              </div>
              <Progress 
                value={(dailyStats.videos_watched_today / dailyStats.daily_limit) * 100} 
                className="h-2 bg-[#1E1E1E]"
              />
              <p className="text-xs text-gray-400 mt-2">
                ⏱️ Tiempo requerido: 1:20 min por video | 💰 Gana RD$0.90 neto (10% comisión)
              </p>
            </CardContent>
          </Card>
        )}

        {/* Video Campaigns Section - Main Task System */}
        {youtubeCampaigns.length > 0 && (
          <Card className="bg-gradient-to-r from-[#FF0000]/20 to-[#CC0000]/20 border-red-500/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Play className="w-5 h-5 text-red-500" />
                <h3 className="font-bold text-lg">Campañas de Video</h3>
                <span className="ml-auto text-xs bg-[#22C55E] text-black px-2 py-1 rounded-full font-bold">
                  +RD$40 c/u
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Haz clic en el link, ve el video 1:20 min, y vuelve a confirmar
              </p>
              <div className="space-y-4">
                {youtubeCampaigns.map((campaign) => {
                  const progress = campaignProgress[campaign.id] || {};
                  const hasStarted = progress.started;
                  const currentDay = progress.current_day || 1;
                  const viewsToday = progress[`views_day${currentDay}`] || 0;
                  const watchTimer = watchTimers[campaign.id] || 0;
                  const isWatching = watchTimer > 0;
                  
                  return (
                    <div key={campaign.id} className="bg-[#1a1a1a] rounded-lg p-4 space-y-3">
                      {/* Campaign Header */}
                      <div className="flex items-start gap-3">
                        <img 
                          src={campaign.thumbnail_url} 
                          alt={campaign.title}
                          className="w-20 h-14 rounded object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="font-bold text-white">{campaign.title}</h4>
                          <p className="text-xs text-gray-400">Por: {campaign.artist_name}</p>
                        </div>
                        <span className="text-[#22C55E] font-bold">+RD$40</span>
                      </div>

                      {!hasStarted ? (
                        /* Start Button */
                        <Button
                          onClick={() => startCampaign(campaign.id)}
                          className="w-full bg-gradient-to-r from-[#FF0000] to-[#CC0000] text-white"
                          disabled={!isAuthenticated}
                        >
                          <Rocket className="w-4 h-4 mr-2" />
                          Comenzar Campaña
                        </Button>
                      ) : progress.is_completed ? (
                        /* Completed */
                        <div className="text-center text-[#22C55E] py-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/30">
                          🎉 ¡Completado! +RD$40 ganados
                        </div>
                      ) : (
                        /* Active Campaign */
                        <div className="space-y-3">
                          {/* Instructions */}
                          <div className="bg-[#0a0a0a] rounded-lg p-3 text-xs text-gray-300">
                            <p className="font-bold text-white mb-2">📋 Instrucciones:</p>
                            <ol className="space-y-1 list-decimal list-inside">
                              <li>Haz clic en &quot;Ir a YouTube&quot;</li>
                              <li>Suscríbete al canal, da like y comenta</li>
                              <li>Ve el video por 1 min 20 seg</li>
                              <li>Sube una captura de pantalla como prueba</li>
                            </ol>
                          </div>

                          {/* YouTube Link Button */}
                          <a 
                            href={campaign.youtube_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            onClick={() => startWatchTimer(campaign.id)}
                            className={`w-full h-12 flex items-center justify-center rounded-md text-white font-medium ${
                              isWatching || viewsToday >= 10 
                                ? 'bg-gray-600 pointer-events-none opacity-50' 
                                : 'bg-[#FF0000] hover:bg-[#CC0000]'
                            }`}
                          >
                            <ExternalLink className="w-5 h-5 mr-2" />
                            {isWatching ? `Viendo... ${Math.floor(watchTimer / 60)}:${String(watchTimer % 60).padStart(2, '0')}` : 'Ir a YouTube'}
                          </a>

                          {/* Timer Progress */}
                          {isWatching && (
                            <div className="space-y-2">
                              <div className="flex justify-between text-xs">
                                <span className="text-yellow-400">⏱️ Viendo video...</span>
                                <span className="text-white font-bold">{Math.floor(watchTimer / 60)}:{String(watchTimer % 60).padStart(2, '0')} / 1:20</span>
                              </div>
                              <Progress value={(watchTimer / 80) * 100} className="h-2" />
                            </div>
                          )}

                          {/* Screenshot Upload Section */}
                          {!isWatching && viewsToday < 10 && (
                            <div className="space-y-3">
                              {/* Hidden file input */}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                ref={el => screenshotInputRefs.current[campaign.id] = el}
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleScreenshotUpload(campaign.id, e.target.files[0]);
                                  }
                                }}
                              />
                              
                              {/* Upload Screenshot Button */}
                              <Button
                                onClick={() => triggerScreenshotInput(campaign.id)}
                                className="w-full bg-gradient-to-r from-[#8B5CF6] to-[#D946EF] text-white font-bold h-12"
                                disabled={uploadingScreenshot[campaign.id] || !canConfirmView[campaign.id]}
                              >
                                {uploadingScreenshot[campaign.id] ? (
                                  <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Subiendo...
                                  </>
                                ) : canConfirmView[campaign.id] ? (
                                  <>
                                    <Camera className="w-5 h-5 mr-2" />
                                    📸 Subir Captura de Pantalla
                                  </>
                                ) : (
                                  <>
                                    <Clock className="w-5 h-5 mr-2" />
                                    Primero ve el video en YouTube
                                  </>
                                )}
                              </Button>
                              
                              {/* Info about screenshot approval */}
                              <div className="p-2 bg-yellow-500/10 rounded-lg border border-yellow-500/30 flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
                                <p className="text-xs text-yellow-300">
                                  Cada captura será revisada por el administrador antes de acreditar tu vista.
                                </p>
                              </div>
                              
                              {/* Show pending/approved screenshots */}
                              {campaignScreenshots[campaign.id]?.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-xs text-gray-400 font-bold">📷 Tus capturas:</p>
                                  <div className="grid grid-cols-3 gap-2">
                                    {campaignScreenshots[campaign.id].slice(0, 6).map((screenshot) => (
                                      <div key={screenshot.id} className="relative">
                                        <img 
                                          src={`${BACKEND_URL}${screenshot.screenshot_url}`}
                                          alt="Captura"
                                          className="w-full h-16 object-cover rounded-lg border border-white/10"
                                        />
                                        <span className={`absolute bottom-0 left-0 right-0 text-[8px] text-center py-0.5 rounded-b-lg ${
                                          screenshot.status === 'approved' 
                                            ? 'bg-[#22C55E] text-black' 
                                            : screenshot.status === 'rejected'
                                            ? 'bg-red-500 text-white'
                                            : 'bg-yellow-500 text-black'
                                        }`}>
                                          {screenshot.status === 'approved' ? '✓ Aprobada' : 
                                           screenshot.status === 'rejected' ? '✗ Rechazada' : '⏳ Pendiente'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Views Progress */}
                          <div className="bg-[#0a0a0a] rounded-lg p-3">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-xs text-gray-400">Vistas Día {currentDay}/3</span>
                              <span className="text-sm font-bold text-white">{viewsToday}/10</span>
                            </div>
                            <Progress value={(viewsToday / 10) * 100} className="h-2" />
                            <div className="flex justify-between text-[10px] text-gray-500 mt-1">
                              <span>Día 1: {progress.views_day1 || 0}/10</span>
                              <span>Día 2: {progress.views_day2 || 0}/10</span>
                              <span>Día 3: {progress.views_day3 || 0}/10</span>
                            </div>
                            {viewsToday >= 10 && currentDay < 3 && (
                              <p className="text-xs text-yellow-400 mt-2 text-center">
                                ✅ Completado hoy - Vuelve mañana para Día {currentDay + 1}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Share Campaigns Section */}
        {shareCampaigns.length > 0 && (
          <Card className="bg-gradient-to-r from-[#E1306C]/20 to-[#F77737]/20 border-[#E1306C]/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Share2 className="w-5 h-5 text-[#E1306C]" />
                <h3 className="font-bold text-lg">Campañas para Compartir</h3>
                <span className="ml-auto text-xs bg-[#22C55E] text-black px-2 py-1 rounded-full font-bold">
                  +RD$30 c/u
                </span>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Comparte en la red social activa y gana RD$30 (1 vez por campaña) - ¡Dura 2h 30min!
              </p>
              <div className="space-y-4">
                {shareCampaigns.map((campaign) => {
                  const activePlatform = campaign.platform?.toLowerCase() || '';
                  const isAlertActive = campaign.is_alert_active !== false; // Default to true if not set
                  return (
                  <div key={campaign.id} className={`relative rounded-lg p-4 space-y-3 ${isAlertActive ? 'bg-[#1a1a1a] ring-2 ring-[#22C55E]/50' : 'bg-[#1a1a1a]'}`}>
                    {/* Alert indicator light */}
                    {isAlertActive && (
                      <div className="absolute -top-2 -right-2 flex items-center gap-1">
                        <span className="relative flex h-4 w-4">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22C55E] opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-4 w-4 bg-[#22C55E]"></span>
                        </span>
                      </div>
                    )}
                    {!isAlertActive && (
                      <div className="absolute -top-2 -right-2">
                        <span className="inline-flex rounded-full h-4 w-4 bg-gray-500"></span>
                      </div>
                    )}
                    {/* Campaign Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-white">{campaign.title}</h4>
                        {isAlertActive && (
                          <span className="text-[10px] bg-[#22C55E] text-black px-1.5 py-0.5 rounded font-bold animate-pulse">ACTIVA</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-[#22C55E] font-bold block">+RD$30</span>
                        {campaign.time_remaining && (
                          <span className={`text-xs font-bold flex items-center gap-1 ${isAlertActive ? 'text-[#22C55E]' : 'text-gray-500'}`}>
                            <Clock className="w-3 h-3" />
                            {campaign.time_remaining}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">Por: {campaign.artist_name}</p>
                    
                    {campaign.description && (
                      <p className="text-sm text-gray-300">{campaign.description}</p>
                    )}
                    
                    {/* URL to share */}
                    <div className="bg-[#0a0a0a] rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">URL para compartir:</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={campaign.share_url}
                          className="flex-1 bg-transparent text-sm text-[#D946EF] truncate outline-none"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(campaign.share_url)}
                          className="shrink-0 hover:bg-white/10"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {/* 4 Social Network Spaces */}
                    <div className="grid grid-cols-4 gap-2">
                      {/* Facebook */}
                      <div 
                        onClick={() => activePlatform === 'facebook' && openShareWindow('facebook', campaign.share_url)}
                        className={`relative p-3 rounded-lg text-center cursor-pointer transition-all ${
                          activePlatform === 'facebook' 
                            ? 'bg-[#1877F2] text-white ring-2 ring-[#22C55E] ring-offset-2 ring-offset-[#1a1a1a]' 
                            : 'bg-[#1877F2]/20 text-[#1877F2]/50'
                        }`}
                      >
                        {activePlatform === 'facebook' && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#22C55E] rounded-full flex items-center justify-center text-[10px]">✓</span>
                        )}
                        <Facebook className="w-6 h-6 mx-auto mb-1" />
                        <p className="text-[10px] font-bold">Facebook</p>
                        {activePlatform === 'facebook' && <p className="text-[8px] mt-1">¡ACTIVA!</p>}
                      </div>

                      {/* Instagram */}
                      <div 
                        onClick={() => activePlatform === 'instagram' && openShareWindow('instagram', campaign.share_url)}
                        className={`relative p-3 rounded-lg text-center cursor-pointer transition-all ${
                          activePlatform === 'instagram' 
                            ? 'bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737] text-white ring-2 ring-[#22C55E] ring-offset-2 ring-offset-[#1a1a1a]' 
                            : 'bg-[#E1306C]/20 text-[#E1306C]/50'
                        }`}
                      >
                        {activePlatform === 'instagram' && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#22C55E] rounded-full flex items-center justify-center text-[10px]">✓</span>
                        )}
                        <Instagram className="w-6 h-6 mx-auto mb-1" />
                        <p className="text-[10px] font-bold">Instagram</p>
                        {activePlatform === 'instagram' && <p className="text-[8px] mt-1">¡ACTIVA!</p>}
                      </div>

                      {/* TikTok */}
                      <div 
                        onClick={() => activePlatform === 'tiktok' && openShareWindow('tiktok', campaign.share_url)}
                        className={`relative p-3 rounded-lg text-center cursor-pointer transition-all ${
                          activePlatform === 'tiktok' 
                            ? 'bg-black text-white border-2 border-white ring-2 ring-[#22C55E] ring-offset-2 ring-offset-[#1a1a1a]' 
                            : 'bg-black/30 text-white/30 border border-white/10'
                        }`}
                      >
                        {activePlatform === 'tiktok' && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#22C55E] rounded-full flex items-center justify-center text-[10px]">✓</span>
                        )}
                        <TikTokIcon className="w-6 h-6 mx-auto mb-1" />
                        <p className="text-[10px] font-bold">TikTok</p>
                        {activePlatform === 'tiktok' && <p className="text-[8px] mt-1">¡ACTIVA!</p>}
                      </div>

                      {/* WhatsApp */}
                      <div 
                        onClick={() => activePlatform === 'whatsapp' && openShareWindow('whatsapp', campaign.share_url, campaign.title)}
                        className={`relative p-3 rounded-lg text-center cursor-pointer transition-all ${
                          activePlatform === 'whatsapp' 
                            ? 'bg-[#25D366] text-white ring-2 ring-[#22C55E] ring-offset-2 ring-offset-[#1a1a1a]' 
                            : 'bg-[#25D366]/20 text-[#25D366]/50'
                        }`}
                      >
                        {activePlatform === 'whatsapp' && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#22C55E] rounded-full flex items-center justify-center text-[10px]">✓</span>
                        )}
                        <span className="text-2xl">💬</span>
                        <p className="text-[10px] font-bold">WhatsApp</p>
                        {activePlatform === 'whatsapp' && <p className="text-[8px] mt-1">¡ACTIVA!</p>}
                      </div>
                    </div>

                    {/* Complete Button */}
                    {!completedCampaigns.includes(campaign.id) ? (
                      <div className="space-y-2 pt-2 border-t border-white/10">
                        <p className="text-xs text-center text-gray-400">
                          ¿Ya compartiste en {campaign.platform || 'redes sociales'}?
                        </p>
                        <Button
                          onClick={() => handleShareCampaign(campaign.id)}
                          className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white font-bold h-12"
                          disabled={!isAuthenticated}
                        >
                          <CheckCircle className="w-5 h-5 mr-2" />
                          ✅ CONFIRMAR - Ya Compartí (+RD$30)
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center text-[#22C55E] text-sm py-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/30">
                        ✅ ¡Completado! +RD$30 agregados a tu balance
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Social Media Section */}
        <div className="mt-8 space-y-6">
          <h2 className="text-2xl font-bold gradient-text">Gana Siguiendo en Redes</h2>
          
          {/* Instagram Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E1306C] to-[#F77737] flex items-center justify-center">
                <Instagram className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Instagram</h3>
              </div>
            </div>
            
            {getTasksByPlatform("instagram").length > 0 ? (
              <div className="space-y-2 pl-2">
                {getTasksByPlatform("instagram").map((task) => {
                  const isCompleted = completedTasks.includes(task.id);
                  const screenshot = taskScreenshots[task.id];
                  const isUploading = uploadingTaskScreenshot[task.id];
                  
                  return (
                    <Card key={task.id} className={`border-white/10 ${isCompleted ? "bg-[#1E1E1E] opacity-50" : "bg-[#121212]"}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">@{task.username}</p>
                            {task.description && <p className="text-xs text-gray-400">{task.description}</p>}
                          </div>
                          {isCompleted ? (
                            <span className="text-[#22C55E] text-sm">✓ Completado</span>
                          ) : (
                            <a href={task.profile_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" className="bg-gradient-to-r from-[#E1306C] to-[#F77737] hover:opacity-90">
                                <ExternalLink className="w-3 h-3 mr-1" /> Seguir
                              </Button>
                            </a>
                          )}
                        </div>
                        
                        {!isCompleted && (
                          <div className="pt-2 border-t border-white/10">
                            {screenshot?.status === 'pending' ? (
                              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                                <Clock className="w-3 h-3" />
                                <span>Captura pendiente de aprobación</span>
                              </div>
                            ) : screenshot?.status === 'rejected' ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-red-400 text-xs">
                                  <AlertCircle className="w-3 h-3" />
                                  <span>Captura rechazada. Sube otra.</span>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Nueva Captura
                                </Button>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Comprobante
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 pl-2">No hay tareas disponibles</p>
            )}
          </div>

          {/* Facebook Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1877F2] to-[#4267B2] flex items-center justify-center">
                <Facebook className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Facebook</h3>
              </div>
            </div>
            
            {getTasksByPlatform("facebook").length > 0 ? (
              <div className="space-y-2 pl-2">
                {getTasksByPlatform("facebook").map((task) => {
                  const isCompleted = completedTasks.includes(task.id);
                  const screenshot = taskScreenshots[task.id];
                  const isUploading = uploadingTaskScreenshot[task.id];
                  
                  return (
                    <Card key={task.id} className={`border-white/10 ${isCompleted ? "bg-[#1E1E1E] opacity-50" : "bg-[#121212]"}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">@{task.username}</p>
                            {task.description && <p className="text-xs text-gray-400">{task.description}</p>}
                          </div>
                          {isCompleted ? (
                            <span className="text-[#22C55E] text-sm">✓ Completado</span>
                          ) : (
                            <a href={task.profile_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" className="bg-gradient-to-r from-[#1877F2] to-[#4267B2] hover:opacity-90">
                                <ExternalLink className="w-3 h-3 mr-1" /> Seguir
                              </Button>
                            </a>
                          )}
                        </div>
                        
                        {!isCompleted && (
                          <div className="pt-2 border-t border-white/10">
                            {screenshot?.status === 'pending' ? (
                              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                                <Clock className="w-3 h-3" />
                                <span>Captura pendiente de aprobación</span>
                              </div>
                            ) : screenshot?.status === 'rejected' ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-red-400 text-xs">
                                  <AlertCircle className="w-3 h-3" />
                                  <span>Captura rechazada. Sube otra.</span>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Nueva Captura
                                </Button>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Comprobante
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 pl-2">No hay tareas disponibles</p>
            )}
          </div>

          {/* TikTok Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#69C9D0] to-[#EE1D52] flex items-center justify-center">
                <TikTokIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">TikTok</h3>
              </div>
            </div>
            
            {getTasksByPlatform("tiktok").length > 0 ? (
              <div className="space-y-2 pl-2">
                {getTasksByPlatform("tiktok").map((task) => {
                  const isCompleted = completedTasks.includes(task.id);
                  const screenshot = taskScreenshots[task.id];
                  const isUploading = uploadingTaskScreenshot[task.id];
                  
                  return (
                    <Card key={task.id} className={`border-white/10 ${isCompleted ? "bg-[#1E1E1E] opacity-50" : "bg-[#121212]"}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">@{task.username}</p>
                            {task.description && <p className="text-xs text-gray-400">{task.description}</p>}
                          </div>
                          {isCompleted ? (
                            <span className="text-[#22C55E] text-sm">✓ Completado</span>
                          ) : (
                            <a href={task.profile_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" className="bg-gradient-to-r from-[#69C9D0] to-[#EE1D52] hover:opacity-90">
                                <ExternalLink className="w-3 h-3 mr-1" /> Seguir
                              </Button>
                            </a>
                          )}
                        </div>
                        
                        {!isCompleted && (
                          <div className="pt-2 border-t border-white/10">
                            {screenshot?.status === 'pending' ? (
                              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                                <Clock className="w-3 h-3" />
                                <span>Captura pendiente de aprobación</span>
                              </div>
                            ) : screenshot?.status === 'rejected' ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-red-400 text-xs">
                                  <AlertCircle className="w-3 h-3" />
                                  <span>Captura rechazada. Sube otra.</span>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Nueva Captura
                                </Button>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Comprobante
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 pl-2">No hay tareas disponibles</p>
            )}
          </div>

          {/* YouTube Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF0000] to-[#CC0000] flex items-center justify-center">
                <Play className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg">YouTube</h3>
              </div>
            </div>
            
            {getTasksByPlatform("youtube").length > 0 ? (
              <div className="space-y-2 pl-2">
                {getTasksByPlatform("youtube").map((task) => {
                  const isCompleted = completedTasks.includes(task.id);
                  const screenshot = taskScreenshots[task.id];
                  const isUploading = uploadingTaskScreenshot[task.id];
                  
                  return (
                    <Card key={task.id} className={`border-white/10 ${isCompleted ? "bg-[#1E1E1E] opacity-50" : "bg-[#121212]"}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium">@{task.username}</p>
                            {task.description && <p className="text-xs text-gray-400">{task.description}</p>}
                          </div>
                          {isCompleted ? (
                            <span className="text-[#22C55E] text-sm">✓ Completado</span>
                          ) : (
                            <a href={task.profile_url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] hover:opacity-90">
                                <ExternalLink className="w-3 h-3 mr-1" /> Suscribirse
                              </Button>
                            </a>
                          )}
                        </div>
                        
                        {!isCompleted && (
                          <div className="pt-2 border-t border-white/10">
                            {screenshot?.status === 'pending' ? (
                              <div className="flex items-center gap-2 text-yellow-400 text-xs">
                                <Clock className="w-3 h-3" />
                                <span>Captura pendiente de aprobación</span>
                              </div>
                            ) : screenshot?.status === 'rejected' ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-red-400 text-xs">
                                  <AlertCircle className="w-3 h-3" />
                                  <span>Captura rechazada. Sube otra.</span>
                                </div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Nueva Captura
                                </Button>
                              </div>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  ref={el => taskScreenshotInputRefs.current[task.id] = el}
                                  onChange={(e) => e.target.files[0] && handleTaskScreenshotUpload(task.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                  onClick={() => triggerTaskScreenshotInput(task.id)}
                                  disabled={isUploading}
                                >
                                  {isUploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Camera className="w-3 h-3 mr-1" />}
                                  Subir Comprobante
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 pl-2">No hay tareas disponibles</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
